import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InboxOutlined, FileOutlined, DeleteOutlined } from "@ant-design/icons";
import { Upload, Button } from "antd";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";

interface ReusableUploadProps {
  accept: string;
  disabled?: boolean;
  loading?: boolean;
  fileList?: UploadFile[];
  onChange?: UploadProps["onChange"];
  onUploadRequest?: (options: {
    file: File;
    onSuccess: (result?: unknown, file?: File) => void;
    onError: (error: unknown) => void;
  }) => void | Promise<void>;
  directory?: boolean;
  height?: number;
  mode?: "compact" | "full";
  hideInputWhenSelected?: boolean;
  fullWidth?: boolean;
  minimal?: boolean;
  showUploadButton?: boolean;
}

const ReusableUpload: React.FC<ReusableUploadProps> = ({
  accept,
  disabled = false,
  loading = false,
  fileList: fileListProp,
  onChange,
  onUploadRequest,
  directory = false,
  height,
  mode = "compact",
  hideInputWhenSelected = false,
  fullWidth = false,
  minimal = false,
  showUploadButton = false,
}) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const draggerContainerRef = useRef<HTMLDivElement | null>(null);
  const pendingRequestArgsRef = useRef<{
    file: any;
    onSuccess: (result?: unknown, file?: File) => void;
    onError: (error: unknown) => void;
  } | null>(null);

  const fileList: UploadFile[] = Array.isArray(fileListProp)
    ? fileListProp
    : [];

  const handleRemoveFile = () => {
    onChange?.({ fileList: [], file: {} as any });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const getDragAreaStyle = () => {
    const isCompact = mode === "compact";
    const baseStyle = {
      borderRadius: 8,
      padding: isCompact ? (minimal ? 4 : 8) : 16,
      height: isCompact ? "auto" : height,
      minHeight: isCompact ? (minimal ? 52 : 80) : height,
      width: isCompact ? (fullWidth ? "100%" : 260) : "100%",
      maxWidth: "100%",
      boxSizing: "border-box" as const,
      border: "2px dashed #e1e5e9",
      background: "#ffffff",
      transition: "all 0.2s ease",
      cursor: "pointer",
      position: "relative" as const,
      overflow: "visible" as const,
    };

    if (isDragOver) {
      return {
        ...baseStyle,
        border: "2px dashed #1890ff",
        background: "linear-gradient(135deg, #f0f8ff 0%, #e6f7ff 100%)",
        transform: "scale(1.01)",
        boxShadow: "0 8px 25px rgba(24, 144, 255, 0.15)",
      };
    }

    return baseStyle;
  };

  const handleClickUploadButton = async () => {
    if (disabled || loading) return;
    if (!onUploadRequest || !pendingRequestArgsRef.current) return;
    try {
      await onUploadRequest(pendingRequestArgsRef.current as any);
    } catch (e) {
      // onUploadRequest should handle errors and call onError
    }
  };

  const renderFilePreview = () => {
    if (fileList.length === 0) return null;

    const file = fileList[0];
    const fileName = file.name || file.originFileObj?.name || "Unknown file";
    const fileSize = file.originFileObj?.size;

    const formatFileSize = (bytes: number) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    return (
      <div
        style={{
          marginTop: 8,
          padding: 8,
          background: "#f8f9fa",
          borderRadius: 6,
          border: "1px solid #e9ecef",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              minWidth: 0,
            }}
          >
            <FileOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 500,
                  color: "#262626",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fileName}
              </div>
              {fileSize && (
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                  {formatFileSize(fileSize)}
                </div>
              )}
            </div>
          </div>
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={handleRemoveFile}
            style={{ marginLeft: 8 }}
          />
        </div>
      </div>
    );
  };

  if (mode === "full") {
    return (
      <div style={{ width: "100%" }}>
        {minimal && (
          <style>
            {`
            .reupload-minimal-upload .ant-upload,
            .reupload-minimal-upload .ant-upload-btn,
            .reupload-minimal-upload .ant-upload-drag {
              padding: 0 !important;
              line-height: 1.1 !important;
            }
          `}
          </style>
        )}
        {!(hideInputWhenSelected && fileList.length > 0) && (
          <div
            ref={draggerContainerRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={getDragAreaStyle()}
          >
            <Upload.Dragger
              className={minimal ? "reupload-minimal-upload" : undefined}
              customRequest={(args) => {
                pendingRequestArgsRef.current = args as any;
                if (!onUploadRequest) {
                  (args as any).onSuccess?.({}, (args as any).file);
                }
              }}
              fileList={fileList}
              onChange={onChange}
              accept={accept}
              maxCount={1}
              disabled={disabled}
              multiple={false}
              directory={directory}
              showUploadList={false}
              style={{ border: "none", background: "transparent", padding: 0 }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  marginBottom: minimal ? 2 : 6,
                }}
              >
                <InboxOutlined
                  style={{
                    fontSize: minimal ? 20 : 32,
                    color: isDragOver ? "#1890ff" : "#8c8c8c",
                    transition: "color 0.2s ease",
                  }}
                />
                <span
                  style={{
                    fontSize: minimal ? 12 : 14,
                    fontWeight: 500,
                    color: isDragOver ? "#1890ff" : "#262626",
                  }}
                >
                  {t("Common-SelectFile")}
                </span>
              </div>
              <div
                style={{
                  fontSize: minimal ? 10 : 12,
                  color: "#8c8c8c",
                  margin: 0,
                  opacity: 0.8,
                  textAlign: "center",
                }}
              >
                {accept}
              </div>
            </Upload.Dragger>
          </div>
        )}

        {showUploadButton && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 8,
            }}
          >
            <Button
              type="primary"
              size="small"
              onClick={handleClickUploadButton}
              disabled={
                disabled || loading || fileList.length === 0 || !onUploadRequest
              }
              loading={loading}
            >
              {t("Common-Upload")}
            </Button>
          </div>
        )}

        {renderFilePreview()}
      </div>
    );
  }

  return (
    <div
      style={{
        display: fullWidth ? "flex" : "inline-flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        minWidth: 0,
        maxWidth: "100%",
        width: fullWidth ? "100%" : undefined,
      }}
    >
      {minimal && (
        <style>
          {`
          .reupload-minimal-upload .ant-upload,
          .reupload-minimal-upload .ant-upload-btn,
          .reupload-minimal-upload .ant-upload-drag {
            padding: 0 !important;
            line-height: 1.1 !important;
          }
        `}
        </style>
      )}
      {!(hideInputWhenSelected && fileList.length > 0) && (
        <>
          <div
            ref={draggerContainerRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={getDragAreaStyle()}
          >
            <Upload.Dragger
              className={minimal ? "reupload-minimal-upload" : undefined}
              customRequest={(args) => {
                pendingRequestArgsRef.current = args as any;
                if (!onUploadRequest) {
                  (args as any).onSuccess?.({}, (args as any).file);
                }
              }}
              fileList={fileList}
              onChange={onChange}
              accept={accept}
              maxCount={1}
              disabled={disabled}
              multiple={false}
              directory={directory}
              showUploadList={false}
              style={{ border: "none", background: "transparent", padding: 0 }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  marginBottom: minimal ? 2 : 4,
                }}
              >
                <InboxOutlined
                  style={{
                    fontSize: minimal ? 16 : 20,
                    color: isDragOver ? "#1890ff" : "#8c8c8c",
                    transition: "color 0.2s ease",
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: isDragOver ? "#1890ff" : "#262626",
                  }}
                >
                  {t("Common-SelectFile")}
                </span>
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#1890ff",
                  margin: 0,
                  fontWeight: 600,
                  backgroundColor: minimal ? "transparent" : "#f0f8ff",
                  padding: minimal ? 0 : "3px 8px",
                  borderRadius: 4,
                  display: "inline-block",
                  border: minimal ? "none" : "1px solid #d6e4ff",
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {accept}
              </div>
            </Upload.Dragger>
          </div>
          {showUploadButton && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 8,
              }}
            >
              <Button
                type="primary"
                size="small"
                onClick={handleClickUploadButton}
                disabled={
                  disabled ||
                  loading ||
                  fileList.length === 0 ||
                  !onUploadRequest
                }
                loading={loading}
              >
                {t("Common-Upload")}
              </Button>
            </div>
          )}
        </>
      )}

      {fileList.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {renderFilePreview()}
        </div>
      )}
    </div>
  );
};

export default ReusableUpload;
